<template>
  <div class="comments-mod tw:flex tw:flex-col tw:gap-10">
    <div class="comments-mod__header tw:flex tw:justify-between tw:items-center">
      <div class="title-box tw:flex tw:justify-between tw:items-center tw:gap-2">
        <TextPlay size="3xl" color="var(--fontcolor)" weight="bold" :text="$t('comments_mod.title')" />
        <IconGoogle name="forum" />
      </div>

      <div>
        <InputPlayV3
          id="comments-mod-search" v-model="searchTextModel" name="comments-mod-search"
          :placeholder="$t('comments_mod.search')" right-icon="search" right-icon-clickable
          @enter="searchText = searchTextModel"
          @right-icon-click="searchText = searchTextModel" />
      </div>
    </div>

    <div class="comments-mod__body">
      <TabsPlayV2 v-model="selectedTab" :tabs="tabs">
        <template #all>
          <CommentsTable :comments="getAllCommentsMod" />
        </template>
        <template #pending>
          <CommentsTable :comments="getUnapprovedCommentsMod" />
        </template>
        <template #approved>
          <CommentsTable :comments="getApprovedCommentsMod" />
        </template>
        <template #unapproved>
          <CommentsTable :comments="getReprovedCommentsMod" />
        </template>
        <template #trash>
          <CommentsTable :comments="getTrashCommentsMod" />
        </template>
      </TabsPlayV2>
    </div>

    <div class="tw:flex tw:justify-end comments-mod__footer">
      <PaginationPlay
        v-if="getLastLoadedCommentsModPage.total > 0 && !getCommentsLoading"
        :total="getLastLoadedCommentsModPage.total" :current-page="currentPage"
        :per-page="getLastLoadedCommentsModPage.per_page" max-visible="4" @on-select-page="currentPage = $event"
        @on-prev="toPage('prev')" @on-next="toPage('next')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { PaginationPlay } from "@/components/atomic";
import CommentsTable from "./CommentsTable.vue";
import { useWatchStore } from "@/store/watch.store";

const { t } = useI18n();
const router = useRouter();
const watchStore = useWatchStore();

const selectedTab = ref("pending");
const currentPage = ref(1);
const searchTextModel = ref("");
const searchText = ref("");

// Clear search when input is emptied
watch(searchTextModel, (val) => {
  if (val === '') searchText.value = '';
});
const _opt = ref("1");
const _checkedValue = ref([]);
const _checkedValue2 = ref([]);
const _checkedValue3 = ref("");
const _checkedValue4 = ref(false);

const statusMap = {
	"pending": "draft",
	"approved": "published",
	"unapproved": "reproved",
};

const mapTabStatus = {
	"all": "all",
	"pending": "draft",
	"approved": "published",
	"unapproved": "reproved",
	"trash": "trash",
};

async function loadComments() {
	await watchStore.handleLoadCommentsModeration({
		page: currentPage.value,
		searchString: searchText.value,
		status: mapTabStatus[selectedTab.value],
	});
}

const getAllCommentsMod = computed(() => watchStore.getAllCommentsMod);
const getApprovedCommentsMod = computed(() => watchStore.getApprovedCommentsMod);
const getReprovedCommentsMod = computed(() => watchStore.getReprovedCommentsMod);
const getUnapprovedCommentsMod = computed(() => watchStore.getUnapprovedCommentsMod);
const getTrashCommentsMod = computed(() => watchStore.getTrashCommentsMod);
const getCountStatusComments = computed(() => watchStore.getCountStatusComments);
const getLastLoadedCommentsModPage = computed(() => watchStore.getLastLoadedCommentsModPage);
const getCommentsLoading = computed(() => watchStore.getLoading.comments);

const tabs = computed(() => [
	{
		id: "all",
		icon: "forum",
		label: t("comments_mod.all", { count: getCountStatusComments.value.all }),
	},
	{
		id: "pending",
		icon: "notifications",
		label: t("comments_mod.pending", { count: getCountStatusComments.value.pending }),
	},
	{
		id: "approved",
		icon: "check_circle",
		label: t("comments_mod.approved", { count: getCountStatusComments.value.approveds }),
	},
	{
		id: "unapproved",
		icon: "cancel",
		label: t("comments_mod.unapproved", { count: getCountStatusComments.value.unapproveds }),
	},
	{ id: "trash", icon: "delete", label: t("comments_mod.trash", { count: getCountStatusComments.value.trash }) },
]);

async function toPage(action: string) {
	if (action === "prev") {
		if (currentPage.value > 1) {
			currentPage.value = currentPage.value - 1;
		}
	} else {
		const lastPage = getLastLoadedCommentsModPage.value?.last_page ?? 1;
		if (currentPage.value < lastPage) {
			currentPage.value = currentPage.value + 1;
		}
	}
}

async function updateFiltersState() {
	await watchStore.actionSetModFilters({
		page: currentPage.value,
		searchString: searchText.value,
		status: mapTabStatus[selectedTab.value],
	});
}

async function loadCommentStatus() {
	await watchStore.actionLoadCommentCountStatus();
}

onMounted(async () => {
	await loadCommentStatus();
	await updateFiltersState();
	await loadComments();
});

watch([searchText, currentPage, selectedTab], async ([newSearch, newPage, newTab]) => {
	router.push({
		query: {
			page: newPage?.toString() ?? "1",
			...(statusMap[newTab] ? { "status[]": statusMap[newTab] } : {}),
			...(newSearch ? { search_string: newSearch } : {}),
		},
	});

	await updateFiltersState();
	await loadComments();
});
</script>
